import { z } from 'zod';
import type { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import type { TF911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911.typings';

const locationUpdateTextBodySchema = z.object({
  caller_id: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  location_time: z.number().optional(),
  created_time: z.number().optional(),
  call_start_time: z.number().optional(),
  uncertainty_radius: z.number().nullable().optional(),
  altitude: z.number().nullable().optional(),
  source: z.string(),
  application: z.string(),
  // Present only in text-to-911 producer messages
  class_of_service_category: z.string().optional(),
  class_of_service: z.string().optional(),
});

const locationUpdateTextMessageSchema = z.object({
  created_time: z.number().optional(),
  body: locationUpdateTextBodySchema,
});

export type LocationUpdateTextBody = z.infer<
  typeof locationUpdateTextBodySchema
>;

/**
 * Enriches a location_update.text WebSocket message body with created_time
 * and call_start_time.
 *
 * These fields are absent from the message body for this message type but are
 * required for hideNearbyLocations deduplication and queue sort order.
 *
 * created_time represents when this specific location update was created and
 * must change per update so hideNearbyLocations deduplication does not silently
 * filter subsequent locations (undefined === undefined → filtered).
 * Resolution: body.created_time → body.location_time → message.created_time.
 *
 * call_start_time is the stable anchor for queue sort order and must not change
 * between location updates for the same call.
 * Resolution: body → initial callQueue location → textFrom911.data.
 *
 * Returns the original body unchanged if schema validation fails.
 */
export const enrichLocationUpdateTextBody = (
  message: { created_time?: number; body: unknown },
  callQueue: Record<string, EmergencyCall>,
  textFrom911Conversations:
    | Record<string, TF911Conversation>
    | null
    | undefined,
): LocationUpdateTextBody | unknown => {
  const parsed = locationUpdateTextMessageSchema.safeParse(message);

  if (!parsed.success) {
    return message.body;
  }

  const { body, created_time: messageCreatedTime } = parsed.data;

  const callerId = body.caller_id.startsWith('+')
    ? body.caller_id.slice(1)
    : body.caller_id;

  const initialLocation = callQueue[callerId]?.core?.locations?.[0];
  const callStartTimeFromQueue =
    initialLocation && 'call_start_time' in initialLocation
      ? initialLocation.call_start_time
      : undefined;
  const tf911Data = textFrom911Conversations?.[callerId]?.data;

  return {
    ...body,
    created_time: body.created_time ?? body.location_time ?? messageCreatedTime,
    call_start_time:
      body.call_start_time ??
      callStartTimeFromQueue ??
      tf911Data?.call_start_time,
  };
};
